import { useGetDirectPayMaterialVendorsQuery } from '@/services/organizations';
import { Box } from '@palmetto/palmetto-components';
import { useCallback } from 'react';

import EventBy from './EventBy';

type VendorEntry = { vendorAlias: string; subCompanyId: string; installerIdentifier: string };
type StateOverrideEntry = VendorEntry & { states: string[] };

const SabalOrganizationDirectPayUpdated = ({ event }: { event: any }) => {
  const { data: vendorData } = useGetDirectPayMaterialVendorsQuery();

  const resolveVendorName = useCallback(
    (alias: string): string => {
      const vendor = vendorData?.materialVendors?.find((v) => v.directPayMaterialVendorAlias === alias);
      return vendor?.directPayMaterialVendorName ?? alias;
    },
    [vendorData],
  );

  const resolveSubCompanyName = useCallback(
    (vendorAlias: string, subCompanyId: string): string | null => {
      if (!subCompanyId) return null;
      const vendor = vendorData?.materialVendors?.find((v) => v.directPayMaterialVendorAlias === vendorAlias);
      return vendor?.subCompanies?.find((sc) => sc.id === subCompanyId)?.name ?? null;
    },
    [vendorData],
  );

  const formatVendorInline = useCallback(
    (entry: VendorEntry): string => {
      const name = resolveVendorName(entry.vendorAlias);
      const sub = resolveSubCompanyName(entry.vendorAlias, entry.subCompanyId);
      const parts: string[] = [];
      if (sub) parts.push(sub);
      else if (entry.subCompanyId) parts.push(`sub-company: ${entry.subCompanyId}`);
      if (entry.installerIdentifier) parts.push(`installer: ${entry.installerIdentifier}`);
      return parts.length > 0 ? `${name} (${parts.join(', ')})` : name;
    },
    [resolveVendorName, resolveSubCompanyName],
  );

  // Legacy fallback for events without newState
  if (!event.data.newState) {
    return (
      <Box display="block">
        <EventBy event={event} /> <strong>{event.data.directPayStatus}</strong> direct pay with{' '}
        <strong>{event.data.directPayVendor}</strong>
      </Box>
    );
  }

  const newState = event.data.newState as {
    directPayDefaultEnabled: boolean;
    vendorAlias: string;
    subCompanyId: string;
    installerIdentifier: string;
    stateOverrides: StateOverrideEntry[];
    optInVendors: VendorEntry[];
  };

  const previousState = event.data.previousState as typeof newState | null;

  // Action verb
  const prevEnabled = previousState?.directPayDefaultEnabled ?? false;
  const nowEnabled = newState.directPayDefaultEnabled;
  let heading: string;
  if (!prevEnabled && nowEnabled) heading = 'Enabled global default';
  else if (prevEnabled && !nowEnabled) heading = 'Disabled global default';
  else heading = 'Updated direct pay settings';

  // Primary vendor diff — also surface details when the toggle itself changed
  const primaryChanged =
    prevEnabled !== nowEnabled ||
    !previousState ||
    previousState.vendorAlias !== newState.vendorAlias ||
    previousState.subCompanyId !== newState.subCompanyId ||
    previousState.installerIdentifier !== newState.installerIdentifier;

  // State overrides diff
  const prevOverrideMap = new Map<string, StateOverrideEntry>(
    (previousState?.stateOverrides ?? []).map((o) => [`${o.vendorAlias}:${o.subCompanyId}`, o]),
  );
  const newOverrideMap = new Map<string, StateOverrideEntry>(
    newState.stateOverrides.map((o) => [`${o.vendorAlias}:${o.subCompanyId}`, o]),
  );

  const addedOverrides: StateOverrideEntry[] = [];
  const removedOverrides: StateOverrideEntry[] = [];
  const modifiedOverrides: Array<{ prev: StateOverrideEntry; next: StateOverrideEntry }> = [];

  newOverrideMap.forEach((next, key) => {
    const prev = prevOverrideMap.get(key);
    if (!prev) {
      addedOverrides.push(next);
    } else if (
      [...prev.states].toSorted().join(',') !== [...next.states].toSorted().join(',') ||
      prev.installerIdentifier !== next.installerIdentifier
    ) {
      modifiedOverrides.push({ prev, next });
    }
  });
  prevOverrideMap.forEach((prev, key) => {
    if (!newOverrideMap.has(key)) removedOverrides.push(prev);
  });

  // Opt-in vendors diff
  const prevOptInMap = new Map<string, VendorEntry>(
    (previousState?.optInVendors ?? []).map((v) => [v.vendorAlias, v]),
  );
  const newOptInMap = new Map<string, VendorEntry>(newState.optInVendors.map((v) => [v.vendorAlias, v]));

  const addedOptIns: VendorEntry[] = [];
  const removedOptIns: VendorEntry[] = [];
  const modifiedOptIns: Array<{ prev: VendorEntry; next: VendorEntry }> = [];
  newOptInMap.forEach((next, key) => {
    const prev = prevOptInMap.get(key);
    if (!prev) {
      addedOptIns.push(next);
    } else if (prev.subCompanyId !== next.subCompanyId || prev.installerIdentifier !== next.installerIdentifier) {
      modifiedOptIns.push({ prev, next });
    }
  });
  prevOptInMap.forEach((v, key) => {
    if (!newOptInMap.has(key)) removedOptIns.push(v);
  });

  return (
    <Box display="block">
      <Box display="block">
        <EventBy event={event} /> <strong>{heading}</strong>
      </Box>

      {primaryChanged && (newState.vendorAlias || previousState?.vendorAlias) && (
        <Box display="block" margin="xs 0 0 0">
          {previousState?.vendorAlias && newState.vendorAlias ? (
            <>
              <strong>Primary vendor:</strong>{' '}
              {formatVendorInline({
                vendorAlias: previousState.vendorAlias,
                subCompanyId: previousState.subCompanyId,
                installerIdentifier: previousState.installerIdentifier,
              })}{' '}
              →{' '}
              {formatVendorInline({
                vendorAlias: newState.vendorAlias,
                subCompanyId: newState.subCompanyId,
                installerIdentifier: newState.installerIdentifier,
              })}
            </>
          ) : previousState?.vendorAlias && !newState.vendorAlias ? (
            <>
              <strong>Primary vendor:</strong>{' '}
              {formatVendorInline({
                vendorAlias: previousState.vendorAlias,
                subCompanyId: previousState.subCompanyId,
                installerIdentifier: previousState.installerIdentifier,
              })}
            </>
          ) : (
            <>
              <strong>Primary vendor:</strong>{' '}
              {formatVendorInline({
                vendorAlias: newState.vendorAlias,
                subCompanyId: newState.subCompanyId,
                installerIdentifier: newState.installerIdentifier,
              })}
            </>
          )}
        </Box>
      )}

      {addedOverrides.map((o) => (
        <Box key={`added-override-${o.vendorAlias}-${o.subCompanyId}`} display="block" margin="xs 0 0 0">
          <strong>Added state override:</strong> {formatVendorInline(o)} — {o.states.join(', ')}
        </Box>
      ))}

      {removedOverrides.map((o) => (
        <Box key={`removed-override-${o.vendorAlias}-${o.subCompanyId}`} display="block" margin="xs 0 0 0">
          <strong>Removed state override:</strong> {formatVendorInline(o)} — {o.states.join(', ')}
        </Box>
      ))}

      {modifiedOverrides.map(({ prev, next }) => (
        <Box key={`modified-override-${prev.vendorAlias}-${prev.subCompanyId}`} display="block" margin="xs 0 0 0">
          <strong>Updated state override:</strong> {formatVendorInline(prev)} → {formatVendorInline(next)}
          {[...prev.states].toSorted().join(',') !== [...next.states].toSorted().join(',') && (
            <>
              {' '}
              — {prev.states.join(', ')} → {next.states.join(', ')}
            </>
          )}
        </Box>
      ))}

      {addedOptIns.map((v) => (
        <Box key={`added-optin-${v.vendorAlias}-${v.subCompanyId}`} display="block" margin="xs 0 0 0">
          <strong>Added opt-in vendor:</strong> {formatVendorInline(v)}
        </Box>
      ))}

      {removedOptIns.map((v) => (
        <Box key={`removed-optin-${v.vendorAlias}-${v.subCompanyId}`} display="block" margin="xs 0 0 0">
          <strong>Removed opt-in vendor:</strong> {formatVendorInline(v)}
        </Box>
      ))}

      {modifiedOptIns.map(({ prev, next }) => (
        <Box key={`modified-optin-${prev.vendorAlias}-${prev.subCompanyId}`} display="block" margin="xs 0 0 0">
          <strong>Updated opt-in vendor:</strong> {formatVendorInline(prev)} → {formatVendorInline(next)}
        </Box>
      ))}
    </Box>
  );
};

export default SabalOrganizationDirectPayUpdated;
